'use client';

import { forwardRef, useRef } from 'react';
import { useProgressInViewport } from '../../utils/useProgressInViewport';
import styles from './FadingGradient.module.css';

type GradientProps = React.ComponentPropsWithoutRef<'div'> & {
  color?: string;
};

const colorVars = (color?: string): React.CSSProperties | undefined =>
  color
    ? ({
        '--fg-color': color,
        '--fg-color-transparent': `${color}00`,
      } as React.CSSProperties)
    : undefined;

export const Gradient = forwardRef<HTMLDivElement, GradientProps>(
  ({ color, className, style, ...rest }, ref) => (
    <div
      ref={ref}
      className={className ? `${styles.gradient} ${className}` : styles.gradient}
      style={{ ...style, ...colorVars(color) }}
      {...rest}
    />
  ),
);
Gradient.displayName = 'Gradient';

export const GradientLeft = forwardRef<HTMLDivElement, GradientProps>(
  ({ color, className, style, ...rest }, ref) => (
    <div
      ref={ref}
      className={
        className ? `${styles.gradientLeft} ${className}` : styles.gradientLeft
      }
      style={{ ...style, ...colorVars(color) }}
      {...rest}
    />
  ),
);
GradientLeft.displayName = 'GradientLeft';

export const FadingGradient = ({
  className,
  children,
  side,
  color,
}: {
  className?: string;
  children: React.ReactNode;
  side?: 'left' | 'right';
  color?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const gradientRef = useRef<HTMLDivElement>(null);

  useProgressInViewport({
    containerRef,
    callback(progression) {
      if (!gradientRef.current || !containerRef.current) {
        return;
      }
      const stopRatio =
        (gradientRef.current.clientHeight * 3) /
        containerRef.current.clientHeight;
      const progress = Math.max(-0.4, progression) + 0.4;
      if (progress < 0) {
        gradientRef.current.style.opacity = '0';
      } else if (progress < 0.1) {
        gradientRef.current.style.opacity = `${progress / 0.1}`;
      } else if (progress > 1.4 - stopRatio) {
        gradientRef.current.style.opacity = `${Math.max(
          0,
          1 - (progress - (1.4 - stopRatio)) / stopRatio,
        )}`;
      } else {
        gradientRef.current.style.opacity = '1';
      }
    },
    startAtZero: false,
  });

  return (
    <div
      className={className ? `${styles.root} ${className}` : styles.root}
      ref={containerRef}
    >
      {children}
      {side === 'left' ? (
        <GradientLeft ref={gradientRef} color={color} />
      ) : (
        <Gradient ref={gradientRef} color={color} />
      )}
    </div>
  );
};
